import { useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Input } from '@/shared/ui/Input';
import { Select } from '@/shared/ui/Select';
import { ApiError } from '@/shared/api/errors';
import { useToast } from '@/shared/ui/Toast';
import { ConfirmDialog } from '@/shared/ui/Modal';
import {
  reviewSmsApi,
  type AppointmentStatus,
  type IngestResult,
  type ManualVisitInput,
  type RosterColumnMapping,
  type RosterSummary,
  type TodayRow,
} from '../api/reviewSmsApi';
import { reviewKeys } from '../queryKeys';
import { errorMessage, useCanDeleteConversations } from './hooks';
import { TypeDeleteDialog } from './TypeDeleteDialog';
import {
  formatDayLabel,
  formatLocalTime,
  ADVANCE_DAYS_VIEWABLE,
  shiftDateKey,
  textStatusLabel,
  VISIT_MAX_AGE_DAYS,
  timeZoneName,
  todayKey,
} from './format';
import styles from './TextRequests.module.css';

const ROSTER_FIELD_LABELS: Record<string, string> = {
  patientName: 'Patient name (full)',
  firstName: 'Patient first name',
  lastName: 'Patient last name',
  mobile: 'Mobile number',
  start: 'Appointment start (date and time)',
  date: 'Appointment date',
  time: 'Appointment time',
  end: 'Appointment end',
  duration: 'Duration (minutes)',
  provider: 'Provider',
  timezone: 'Patient timezone',
  consent: 'OK to text',
  externalRef: 'Appointment id',
};

function TextCell({
  row,
  onOpenConversation,
}: {
  row: TodayRow;
  onOpenConversation: (id: string) => void;
}) {
  const t = row.text;
  // "sent" here means the visit reached Betty and its invite exists; delivery lives on the thread.
  if (t.kind === 'sent') {
    return row.conversationId ? (
      <button
        type="button"
        className={styles.pillLink}
        onClick={() => onOpenConversation(row.conversationId!)}
      >
        Text created · open conversation →
      </button>
    ) : (
      <span className={styles.pillAccent}>Text created · see Conversations</span>
    );
  }
  if (t.kind === 'queued') return <span className={styles.pillAccent}>Handed to Betty</span>;
  if (t.kind === 'waiting_confirmation')
    return <span className={styles.pillSetup}>Waiting for confirmation</span>;
  return <span className={styles.cellMuted}>{textStatusLabel(t)}</span>;
}

/** Rows the upload did not add (backend `RowIssue`). */
const ROW_ISSUE_LABEL: Record<string, string> = {
  no_mobile: 'without a mobile number',
  invalid_number: 'with an invalid number',
  missing_time: 'without a time',
  missing_name: 'without a name',
  duplicate: 'duplicate',
  too_old: `more than ${VISIT_MAX_AGE_DAYS} days ago`,
};

/** Why an added visit won't be texted (backend `RosterSummary.skipped`). */
const SKIP_REASON_SENTENCE: Record<string, string> = {
  landline: 'Landline, can’t text.',
  no_consent: 'Not OK to text, so no text.',
  recently_contacted: 'Already sent a review request in the last 60 days, so no text this time.',
  suppressed: 'This number opted out of texts.',
  open_conversation: 'Already in a conversation with Betty, so no new text.',
};

/** The toast for one added visit: "Added." plus why it won't be texted, if it won't. */
function visitAddedMessage(s: RosterSummary): { text: string; warn: boolean } {
  const issue = Object.entries(s.issues).find(([, n]) => n > 0)?.[0];
  if (issue) return { text: `Not added: ${ROW_ISSUE_LABEL[issue] ?? issue}.`, warn: true };
  const verb = s.updated > 0 ? 'Updated the visit already on the schedule.' : 'Added.';
  const skip = Object.entries(s.skipped).find(([, n]) => n > 0)?.[0];
  return skip
    ? { text: `${verb} ${SKIP_REASON_SENTENCE[skip] ?? skip.replace('_', ' ')}`, warn: true }
    : { text: `${verb} Betty will text after you confirm the visit.`, warn: false };
}

/** The 400 message, or the field messages of a 422 validation error. */
function visitErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.data as { error?: { errors?: Record<string, string[]> } } | undefined;
    const fields = body?.error?.errors;
    if (fields && Object.keys(fields).length > 0)
      return Object.entries(fields)
        .map(([field, msgs]) => `${field}: ${msgs.join(', ')}`)
        .join(' · ');
  }
  return errorMessage(err);
}

const EMPTY_VISIT = {
  firstName: '',
  lastName: '',
  mobile: '',
  time: '',
  minutes: '60',
  provider: '',
  consent: 'unknown' as ManualVisitInput['consent'],
};

/** One visit typed in by hand; ingested exactly like a one-row upload. */
function AddVisitForm({
  locationId,
  viewDate,
  onAdded,
}: {
  locationId: string;
  /** The day Today is showing: the date field follows it until someone picks another. */
  viewDate: string;
  onAdded: (date: string) => void;
}) {
  const toast = useToast();
  const [form, setForm] = useState(EMPTY_VISIT);
  const [pickedDate, setPickedDate] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const date = pickedDate ?? viewDate;
  const set =
    (field: keyof typeof EMPTY_VISIT) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));

  const add = useMutation({
    mutationFn: (visit: ManualVisitInput) => reviewSmsApi.addVisit(locationId, visit),
    onSuccess: (result, visit) => {
      const message = visitAddedMessage(result.summary);
      if (message.warn) toast.warning(message.text);
      else toast.success(message.text);
      setForm(EMPTY_VISIT);
      setPickedDate(null);
      setError(null);
      onAdded(visit.date);
    },
    onError: (err: unknown) => setError(visitErrorMessage(err)),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const minutes = Number(form.minutes);
    add.mutate({
      firstName: form.firstName.trim(),
      ...(form.lastName.trim() ? { lastName: form.lastName.trim() } : {}),
      mobile: form.mobile.trim(),
      date,
      time: form.time,
      ...(form.minutes.trim() && Number.isInteger(minutes) && minutes > 0 ? { minutes } : {}),
      ...(form.provider.trim() ? { provider: form.provider.trim() } : {}),
      consent: form.consent,
    });
  };

  return (
    <form className={styles.visitForm} onSubmit={submit} noValidate>
      <div className={styles.visitGrid}>
        <Input
          id="add-visit-first-name"
          label="First name"
          value={form.firstName}
          onChange={set('firstName')}
          required
          autoComplete="off"
        />
        <Input
          id="add-visit-last-name"
          label="Last name or initial (optional)"
          value={form.lastName}
          onChange={set('lastName')}
          autoComplete="off"
        />
        <Input
          id="add-visit-mobile"
          label="Mobile"
          type="tel"
          value={form.mobile}
          onChange={set('mobile')}
          placeholder="510-555-0142"
          required
          autoComplete="off"
        />
        <Input
          id="add-visit-date"
          label="Date"
          type="date"
          value={date}
          onChange={(e) => setPickedDate(e.target.value || null)}
          required
        />
        <Input
          id="add-visit-time"
          label="Start time"
          type="time"
          value={form.time}
          onChange={set('time')}
          required
        />
        <Input
          id="add-visit-minutes"
          label="Length (minutes)"
          type="number"
          min={5}
          max={480}
          step={5}
          value={form.minutes}
          onChange={set('minutes')}
        />
        <Input
          id="add-visit-provider"
          label="Provider (optional)"
          value={form.provider}
          onChange={set('provider')}
          autoComplete="off"
        />
        <Select
          id="add-visit-consent"
          label="OK to text"
          value={form.consent}
          options={[
            { value: 'unknown', label: 'Didn’t ask (we’ll still text)' },
            { value: 'yes', label: 'Yes, they agreed' },
          ]}
          hint="Every first text ends with “Reply STOP to opt out”."
          onChange={set('consent')}
        />
      </div>
      {error && (
        <Callout variant="error" title="Not added">
          {error}
        </Callout>
      )}
      <div className={styles.dropActions}>
        <Button
          type="submit"
          size="sm"
          isLoading={add.isPending}
          disabled={!form.firstName.trim() || !form.mobile.trim() || !form.time || !date}
        >
          Add visit
        </Button>
      </div>
    </form>
  );
}

function skipped(row: TodayRow): boolean {
  return row.text.kind === 'skipped';
}

/** Upload / paste with the one-time column mapping confirm (PRD 4.2). */
function RosterIntake({
  locationId,
  viewDate,
  onDone,
  onVisitAdded,
}: {
  locationId: string;
  viewDate: string;
  onDone: () => void;
  onVisitAdded: (date: string) => void;
}) {
  const toast = useToast();
  const [mode, setMode] = useState<'drop' | 'paste' | 'visit' | 'mapping'>('drop');
  const [pasteText, setPasteText] = useState('');
  const [pending, setPending] = useState<{ file?: File; text?: string } | null>(null);
  const [mappingResult, setMappingResult] = useState<Extract<
    IngestResult,
    { needsMapping: true }
  > | null>(null);
  const [mapping, setMapping] = useState<RosterColumnMapping>({});
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const handle = (result: IngestResult) => {
    if (result.needsMapping) {
      setMappingResult(result);
      setMapping(result.suggestedMapping);
      setMode('mapping');
      return;
    }
    const s = result.summary;
    const skips = Object.entries(s.skipped)
      .filter(([, n]) => n > 0)
      .map(([k, n]) => `${n} ${k.replace('_', ' ')}`)
      .join(', ');
    const notAdded = Object.entries(s.issues)
      .filter(([, n]) => n > 0)
      .map(([k, n]) => `${n} ${ROW_ISSUE_LABEL[k] ?? k.replace('_', ' ')}`)
      .join(', ');
    const message = `Parsed ${s.rows} rows: ${s.textable} textable${skips ? ` · skipped ${skips}` : ''}${notAdded ? ` · not added: ${notAdded}` : ''}.`;
    if (s.issues.too_old > 0) toast.warning(message);
    else toast.success(message);
    setPending(null);
    setMappingResult(null);
    setPasteText('');
    setMode('drop');
    onDone();
  };

  const ingest = useMutation({
    mutationFn: (input: {
      file?: File;
      text?: string;
      mapping?: RosterColumnMapping;
      save?: boolean;
    }) =>
      input.file
        ? reviewSmsApi.uploadRoster(locationId, input.file, input.mapping, input.save)
        : reviewSmsApi.pasteRoster(locationId, input.text ?? '', input.mapping, input.save),
    onSuccess: handle,
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });

  const submitFile = (file: File) => {
    setPending({ file });
    ingest.mutate({ file });
  };
  const submitPaste = () => {
    setPending({ text: pasteText });
    ingest.mutate({ text: pasteText });
  };
  const confirmMapping = () => ingest.mutate({ ...(pending ?? {}), mapping, save: true });

  if (mode === 'mapping' && mappingResult) {
    const options = [
      { value: '', label: '— not in this file —' },
      ...mappingResult.headers.map((h) => ({ value: h, label: h })),
    ];
    return (
      <div className={styles.uploadCard}>
        <div className={styles.stepHeader}>
          <h3 className={styles.stepHeading}>Match the columns</h3>
          <span className={styles.hint}>Once; we remember it for this location</span>
        </div>
        {mappingResult.missing.length > 0 && (
          <Callout variant="warning">
            We need: {mappingResult.missing.map((m) => m.label).join(', ')}.
          </Callout>
        )}
        <div className={styles.mappingGrid}>
          {Object.keys(ROSTER_FIELD_LABELS).map((field) => (
            <Select
              key={field}
              label={ROSTER_FIELD_LABELS[field]}
              value={mapping[field] ?? ''}
              options={options}
              onChange={(e) => setMapping((m) => ({ ...m, [field]: e.target.value || null }))}
            />
          ))}
        </div>
        {mappingResult.sampleRows[0] && (
          <p className={styles.hint}>
            First row:{' '}
            {mappingResult.headers
              .map((h) => `${h}: ${mappingResult.sampleRows[0][h] ?? ''}`)
              .join(' · ')}
          </p>
        )}
        <div className={styles.row}>
          <Button variant="ghost" size="sm" onClick={() => setMode('drop')}>
            Cancel
          </Button>
          <Button size="sm" isLoading={ingest.isPending} onClick={confirmMapping}>
            Import with these columns
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.uploadCard}>
      <div className={styles.stepHeader}>
        <h3 className={styles.stepHeading}>Add appointments</h3>
        <div className={styles.subTabs}>
          <button
            type="button"
            className={clsx(styles.subTab, mode === 'drop' && styles.subTabActive)}
            onClick={() => setMode('drop')}
          >
            Upload file
          </button>
          <button
            type="button"
            className={clsx(styles.subTab, mode === 'paste' && styles.subTabActive)}
            onClick={() => setMode('paste')}
          >
            Paste rows
          </button>
          <button
            type="button"
            className={clsx(styles.subTab, mode === 'visit' && styles.subTabActive)}
            onClick={() => setMode('visit')}
          >
            Add a visit
          </button>
        </div>
      </div>
      {mode === 'visit' ? (
        <p className={styles.muted}>
          One visit by hand. It’s checked like an uploaded row: landlines, patients texted in the
          last 60 days and visits more than {VISIT_MAX_AGE_DAYS} days ago aren’t texted.
        </p>
      ) : (
        <p className={styles.muted}>
          Drop a CSV or Excel export from your PMS (Dentrix, Open Dental, Eaglesoft…). We need name,
          mobile number, appointment time. Provider and “OK to text” columns are optional. Best:
          upload before the day starts (tomorrow’s or next week’s schedule works too). End of day or
          catching up later is fine, up to {VISIT_MAX_AGE_DAYS} days after the visit; older visits
          aren’t added. Uploading the same file twice won’t double-text anyone.
        </p>
      )}
      {mode === 'visit' ? (
        <AddVisitForm
          locationId={locationId}
          viewDate={viewDate}
          onAdded={(date) => {
            onDone();
            onVisitAdded(date);
          }}
        />
      ) : mode === 'drop' ? (
        <div
          className={clsx(styles.dropZone, dragOver && styles.dropZoneActive)}
          role="button"
          tabIndex={0}
          onClick={() => fileInput.current?.click()}
          onKeyDown={(e) => e.key === 'Enter' && fileInput.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const file = e.dataTransfer.files[0];
            if (file) submitFile(file);
          }}
        >
          {ingest.isPending
            ? 'Reading the file…'
            : 'Drop your schedule here, or click to choose a file'}
          <input
            ref={fileInput}
            type="file"
            accept=".csv,.xls,.xlsx,text/csv"
            className={styles.fileInput}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) submitFile(file);
              e.target.value = '';
            }}
          />
        </div>
      ) : (
        <>
          <textarea
            className={styles.textarea}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={
              'Patient\tMobile\tTime\tProvider\nMaria Gomez\t(510) 555-0142\t10:30 AM\tDr. Patel'
            }
            aria-label="Pasted rows"
          />
          <p className={styles.hint}>
            Paste from your PMS with its header row, or type one visit per line without headers:
            name, mobile, time, provider (e.g. “Maria 510-555-0142 10:30a Dr. Patel”). Lines without
            a date are today.
          </p>
          <div className={styles.dropActions}>
            <Button
              size="sm"
              isLoading={ingest.isPending}
              disabled={!pasteText.trim()}
              onClick={submitPaste}
            >
              Import rows
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export function TodayTab({
  locationId,
  onOpenConversation,
}: {
  locationId: string;
  onOpenConversation: (conversationId: string) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [date, setDate] = useState<string | undefined>(undefined);
  const day = useQuery({
    queryKey: reviewKeys.smsDay(locationId, date),
    queryFn: () => reviewSmsApi.getDay(locationId, date),
    enabled: !!locationId,
    refetchInterval: 60_000,
  });
  const invalidateDays = () =>
    queryClient.invalidateQueries({ queryKey: reviewKeys.smsDays(locationId) });
  // New visits can clear a heads-up ("no uploads lately") or raise one, so refresh those too.
  const onRosterChanged = () => {
    invalidateDays();
    queryClient.invalidateQueries({ queryKey: reviewKeys.smsHeadsUps(locationId) });
  };

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: AppointmentStatus }) =>
      reviewSmsApi.setAppointmentStatus(locationId, id, status),
    onSuccess: (r) => {
      invalidateDays();
      if (r.published === 'review_sms.visit_completed')
        toast.success('Confirmed. Betty will text after the visit.');
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });
  // Moving a confirmed visit off Completed once its text exists cancels that conversation, so ask.
  const [pendingChange, setPendingChange] = useState<{
    id: string;
    patient: string;
    status: AppointmentStatus;
  } | null>(null);
  const changeStatus = (row: TodayRow, next: AppointmentStatus) => {
    const textExists = row.text.kind === 'sent' || row.conversationId !== null;
    if (row.status === 'completed' && next !== 'completed' && textExists)
      setPendingChange({ id: row.id, patient: row.patient, status: next });
    else setStatus.mutate({ id: row.id, status: next });
  };
  const confirmAll = useMutation({
    mutationFn: (date: string) => reviewSmsApi.confirmFinished(locationId, date),
    onSuccess: (r) => {
      invalidateDays();
      toast.success(
        `${r.confirmed} visit${r.confirmed === 1 ? '' : 's'} confirmed. Texts scheduled.`,
      );
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });

  const view = day.data;
  const tz = view?.timezone ?? 'America/Los_Angeles';
  const current = date ?? view?.date ?? todayKey(tz);
  const today = todayKey(tz);
  const isToday = current === today;
  // Back as far as visits can still be confirmed; forward for schedules uploaded in advance.
  const atOldest = current <= shiftDateKey(today, -VISIT_MAX_AGE_DAYS);
  const atNewest = current >= shiftDateKey(today, ADVANCE_DAYS_VIEWABLE);
  const rows = useMemo(() => view?.rows ?? [], [view]);
  const unconfirmed = view?.unconfirmed ?? 0;

  // Owners and admins can clear test visits (with their test conversations) in bulk. Patient
  // visits are never deleted here: "Don't text" is the control for those.
  const canDelete = useCanDeleteConversations();
  const testRows = rows.filter((r) => r.isTest);
  const selectable = canDelete && testRows.length > 0;
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const selected = testRows.filter((r) => picked.has(r.id));
  const toggle = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const bulkDelete = useMutation({
    mutationFn: () =>
      reviewSmsApi.bulkDeleteTestVisits(
        locationId,
        selected.map((r) => r.id),
      ),
    onSuccess: (r) => {
      setConfirmBulk(false);
      setPicked(new Set());
      invalidateDays();
      queryClient.invalidateQueries({ queryKey: reviewKeys.smsInboxes(locationId) });
      if (r.failed.length > 0)
        toast.warning(
          `${r.deleted} deleted; ${r.failed.length} couldn’t be: ${r.failed[0].message}`,
        );
      else toast.success(`${r.deleted} test visit${r.deleted === 1 ? '' : 's'} deleted.`);
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });

  return (
    <div className={styles.root}>
      {unconfirmed > 0 ? (
        <Callout
          variant="warning"
          title={`${unconfirmed} finished visit${unconfirmed === 1 ? '' : 's'} still need${unconfirmed === 1 ? 's' : ''} confirming.`}
          actions={
            <Button
              size="sm"
              isLoading={confirmAll.isPending}
              onClick={() => confirmAll.mutate(current)}
            >
              Mark all finished visits completed
            </Button>
          }
        >
          Texts only go out for visits you mark completed.
        </Callout>
      ) : (
        rows.length > 0 && <Callout variant="info">All finished visits are confirmed.</Callout>
      )}

      <RosterIntake
        locationId={locationId}
        viewDate={current}
        onDone={onRosterChanged}
        onVisitAdded={(visitDate) => {
          if (visitDate !== current) setDate(visitDate === today ? undefined : visitDate);
        }}
      />

      <div className={styles.todayHeader}>
        <div className={styles.dayNav}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDate(shiftDateKey(current, -1))}
            disabled={atOldest}
          >
            ← Previous day
          </Button>
          <span className={styles.dayLabel}>
            {isToday ? 'Today · ' : ''}
            {formatDayLabel(current, tz)}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const next = shiftDateKey(current, 1);
              setDate(next === today ? undefined : next);
            }}
            disabled={atNewest}
          >
            Next day →
          </Button>
          {!isToday && (
            <Button variant="ghost" size="sm" onClick={() => setDate(undefined)}>
              Today
            </Button>
          )}
        </div>
        <span className={styles.hint}>
          {rows.length} appointment{rows.length === 1 ? '' : 's'} · times in {timeZoneName(tz)}
          {view ? ' (change in Setup → When texts go out)' : ''}
        </span>
      </div>

      {selectable && (
        <div className={styles.bulkBar}>
          <button
            type="button"
            className={styles.textButton}
            onClick={() =>
              setPicked(
                selected.length === testRows.length
                  ? new Set()
                  : new Set(testRows.map((r) => r.id)),
              )
            }
          >
            {selected.length === testRows.length
              ? 'Clear selection'
              : `Select all tests (${testRows.length})`}
          </button>
          {selected.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setConfirmBulk(true)}>
              Delete {selected.length} test visit{selected.length === 1 ? '' : 's'}
            </Button>
          )}
          <span className={styles.hint}>Only test visits can be deleted.</span>
        </div>
      )}
      <TypeDeleteDialog
        isOpen={confirmBulk}
        onClose={() => setConfirmBulk(false)}
        onConfirm={() => bulkDelete.mutate()}
        isLoading={bulkDelete.isPending}
        title={`Delete ${selected.length} test visit${selected.length === 1 ? '' : 's'}?`}
      >
        <p className={styles.hint}>
          {selected.length === 1 ? 'The test visit and its' : 'These test visits and their'} test
          conversation{selected.length === 1 ? '' : 's'} are removed completely. Your numbers don’t
          change, because tests never count. This can’t be undone.
        </p>
      </TypeDeleteDialog>

      <ConfirmDialog
        isOpen={pendingChange !== null}
        onClose={() => setPendingChange(null)}
        onConfirm={() => {
          if (pendingChange)
            setStatus.mutate(
              { id: pendingChange.id, status: pendingChange.status },
              { onSettled: () => setPendingChange(null) },
            );
        }}
        title={`Change ${pendingChange?.patient ?? 'this visit'} from Completed?`}
        message="Betty already created the review text for this visit. Changing it cancels that text and closes the conversation with this patient."
        confirmLabel="Change and cancel the text"
        variant="destructive"
        isLoading={setStatus.isPending}
      />

      <div className={styles.table}>
        <div className={clsx(styles.tableHeader, selectable && styles.tableSelectable)}>
          {selectable && <span />}
          <span>Time</span>
          <span>Patient</span>
          <span>Provider</span>
          <span>Mobile</span>
          <span>Visit happened?</span>
          <span>Text goes out</span>
        </div>
        {day.isLoading && <div className={styles.tableEmpty}>Loading…</div>}
        {!day.isLoading && rows.length === 0 && (
          <div className={styles.tableEmpty}>
            {atOldest
              ? `Visits more than ${VISIT_MAX_AGE_DAYS} days ago can’t be uploaded or texted.`
              : 'No appointments for this day yet. Upload or paste the schedule above.'}
          </div>
        )}
        {rows.map((row) => {
          const dim = skipped(row);
          const status = row.status;
          return (
            <div
              key={row.id}
              className={clsx(
                styles.tableRow,
                selectable && styles.tableSelectable,
                dim && styles.tableRowDim,
              )}
            >
              {selectable && (
                <span>
                  {row.isTest && (
                    <input
                      type="checkbox"
                      className={styles.bulkCheckBox}
                      checked={picked.has(row.id)}
                      onChange={() => toggle(row.id)}
                      aria-label={`Select test visit at ${formatLocalTime(row.startsAt, tz)}`}
                    />
                  )}
                </span>
              )}
              <span className={styles.cellSecondary}>
                {/* The practice's zone for every row (not each row's own), so the column reads as
                    one schedule and matches the day the front desk is looking at. */}
                {formatLocalTime(row.startsAt, tz)}
              </span>
              <span className={styles.cellPrimary}>
                {row.patient}
                {row.isTest && <span className={styles.pillInfo}> Test</span>}
              </span>
              <span className={styles.cellSecondary}>{row.providerName ?? '—'}</span>
              <span className={styles.mono}>
                {row.lineType === 'landline' ? 'landline' : row.mobileMasked}
              </span>
              <span>
                {dim ? (
                  <span className={styles.pillMuted}>Skipped</span>
                ) : (
                  <span
                    className={styles.segment}
                    role="group"
                    aria-label={`Visit status for ${row.patient}`}
                  >
                    <button
                      type="button"
                      className={clsx(
                        styles.segmentButton,
                        status === 'completed' && styles.segmentOnDone,
                      )}
                      aria-pressed={status === 'completed'}
                      onClick={() =>
                        changeStatus(row, status === 'completed' ? 'scheduled' : 'completed')
                      }
                      disabled={setStatus.isPending}
                    >
                      Completed
                    </button>
                    <button
                      type="button"
                      className={clsx(
                        styles.segmentButton,
                        status === 'no_show' && styles.segmentOnNoShow,
                      )}
                      aria-pressed={status === 'no_show'}
                      onClick={() =>
                        changeStatus(row, status === 'no_show' ? 'scheduled' : 'no_show')
                      }
                      disabled={setStatus.isPending}
                    >
                      No-show
                    </button>
                    <button
                      type="button"
                      className={clsx(
                        styles.segmentButton,
                        status === 'excluded' && styles.segmentOnSkip,
                      )}
                      aria-pressed={status === 'excluded'}
                      onClick={() =>
                        changeStatus(row, status === 'excluded' ? 'scheduled' : 'excluded')
                      }
                      disabled={setStatus.isPending}
                    >
                      Don’t text
                    </button>
                  </span>
                )}
              </span>
              <span>
                <TextCell row={row} onOpenConversation={onOpenConversation} />
              </span>
            </div>
          );
        })}
      </div>
      <p className={styles.hint}>
        Send time = visit end + your delay, moved into the patient’s window; a visit confirmed later
        (up to {VISIT_MAX_AGE_DAYS} days) is texted shortly after you confirm it. Anyone who texted
        STOP, got a request in the last 60 days, or is marked “don’t text” is skipped automatically.
      </p>
    </div>
  );
}
